/**
 * MetadataInspector Component
 *
 * Developer debugging tool that shows entity metadata in a Drawer.
 * Accessible from the Dev dropdown menu.
 */

import React from 'react'
import { Drawer, Button, Space, Typography, Tooltip } from 'antd'
import {
  DatabaseOutlined,
  ReloadOutlined,
  CloseOutlined,
  FullscreenOutlined,
  FullscreenExitOutlined
} from '@ant-design/icons'
import { SchemaList } from './SchemaList'
import { SchemaDetail } from './SchemaDetail'
import { useMetadataInspector } from './hooks/useMetadataInspector'
import type { MetadataInspectorProps } from './types'

const { Title } = Typography

/**
 * CSS styles for the component
 * Injected into head on first render
 */
const STYLES = `
  .metadata-inspector-system-row {
    background: #fafafa;
    opacity: 0.8;
  }
  .metadata-inspector-system-row:hover {
    background: #f5f5f5;
  }
  .metadata-inspector-danger-row {
    background: #fff2f0;
  }
  .metadata-inspector-danger-row:hover {
    background: #ffebe8 !important;
  }
  .metadata-inspector .ant-tabs-content {
    height: calc(100% - 46px);
    overflow: auto;
    padding: 16px 24px;
  }
  .metadata-inspector .ant-tabs {
    height: 100%;
  }
`

// Inject styles once
let stylesInjected = false
function injectStyles() {
  if (stylesInjected) return
  const style = document.createElement('style')
  style.textContent = STYLES
  document.head.appendChild(style)
  stylesInjected = true
}

export function MetadataInspector({
  open,
  onClose
}: MetadataInspectorProps): React.ReactElement {
  // Inject CSS on first render
  React.useEffect(() => {
    injectStyles()
  }, [])

  // State management hook
  const {
    entitiesLoading,
    selectedEntity,
    selectEntity,
    searchTerm,
    setSearchTerm,
    filteredEntities,
    entityMetadata,
    metadataLoading,
    metadataError,
    refreshEntities,
    refreshMetadata
  } = useMetadataInspector()

  // Fullscreen state
  const [fullscreen, setFullscreen] = React.useState(false)

  // Handle close and reset
  const handleClose = () => {
    selectEntity(null)
    setSearchTerm('')
    onClose()
  }

  // Drawer title
  const drawerTitle = (
    <Space>
      <DatabaseOutlined style={{ color: '#1890ff' }} />
      <Title level={5} style={{ margin: 0 }}>Metadata Inspector</Title>
    </Space>
  )

  // Drawer extra actions
  const drawerExtra = (
    <Space>
      <Tooltip title="Refresh entities">
        <Button
          type="text"
          icon={<ReloadOutlined spin={entitiesLoading} />}
          onClick={() => {
            refreshEntities()
            if (selectedEntity) {
              refreshMetadata()
            }
          }}
          size="small"
        />
      </Tooltip>
      <Tooltip title={fullscreen ? 'Exit fullscreen' : 'Fullscreen'}>
        <Button
          type="text"
          icon={fullscreen ? <FullscreenExitOutlined /> : <FullscreenOutlined />}
          onClick={() => setFullscreen(!fullscreen)}
          size="small"
        />
      </Tooltip>
      <Button
        type="text"
        icon={<CloseOutlined />}
        onClick={handleClose}
        size="small"
      />
    </Space>
  )

  return (
    <Drawer
      title={drawerTitle}
      placement="right"
      onClose={handleClose}
      open={open}
      width={fullscreen ? '100%' : '80%'}
      styles={{
        body: {
          padding: 0,
          height: '100%',
          overflow: 'hidden'
        },
        header: {
          borderBottom: '1px solid #f0f0f0'
        }
      }}
      extra={drawerExtra}
      closable={false}
      destroyOnHidden={false}
      className="metadata-inspector"
    >
      <div style={{
        display: 'flex',
        height: '100%'
      }}>
        {/* Left panel - Entity list */}
        <div style={{
          width: '280px',
          minWidth: '280px',
          borderRight: '1px solid #f0f0f0',
          background: '#fff',
          display: 'flex',
          flexDirection: 'column'
        }}>
          <SchemaList
            entities={filteredEntities}
            selectedEntityPath={selectedEntity?.path || null}
            onSelect={selectEntity}
            isLoading={entitiesLoading}
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
          />
        </div>

        {/* Right panel - Entity details */}
        <div style={{
          flex: 1,
          background: '#fff',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column'
        }}>
          <SchemaDetail
            entity={selectedEntity}
            metadata={entityMetadata}
            isLoading={metadataLoading}
            error={metadataError}
          />
        </div>
      </div>
    </Drawer>
  )
}

export default MetadataInspector
